// ---------------------------------------------------------------------------
// Calendar: meetings and tasks, plus the deal dates that matter (close date, internal quotation deadline,
// tender submission deadline, expected PO date), for one person or the whole team.
// ---------------------------------------------------------------------------
import { z } from 'zod';

export const CALENDAR_TYPES = [
  { value: 'meeting', label: 'Meetings', color: 'purple' },
  { value: 'task', label: 'Tasks & calls', color: 'green' },
  { value: 'close', label: 'Close dates', color: 'blue' },
  { value: 'quote_deadline', label: 'Quotation deadlines', color: 'orange' },
  { value: 'submission', label: 'Tender submissions', color: 'red' },
  { value: 'po', label: 'Expected POs', color: 'teal' },
  { value: 'site', label: 'Site assignments', color: 'brown' },
] as const;
export type CalendarType = (typeof CALENDAR_TYPES)[number]['value'];

export interface CalendarItem {
  /** Unique in the view (activity id, or deal id + date kind). */
  key: string;
  type: CalendarType;
  title: string;
  /** Day it's shown on (YYYY-MM-DD, Riyadh time). */
  date: string;
  /** Meetings: start and end times. */
  startAt: string | null;
  endAt: string | null;
  done: boolean;
  ownerName: string | null;
  /** The activity, for meetings and tasks. */
  activityId: string | null;
  /** The record to open (the deal for deal dates; the related record for activities, when any). */
  objectName: 'opportunity' | 'account' | 'contact' | 'lead' | 'delivery_project' | null;
  recordId: string | null;
  /** The deal or customer it's about, for context. */
  about: string | null;
  /** Meetings and tasks: what the edit form needs. */
  description?: string | null;
  location?: string | null;
  ownerId?: string | null;
  /** Tasks: 'high' or 'normal' (a High task is marked in the calendar). */
  priority?: string | null;
  /** Meetings: invited CRM users and their answers; myResponse when the viewer is one of them. */
  guests?: { id: string; name: string; response: 'pending' | 'accepted' | 'declined' }[];
  myResponse?: 'pending' | 'accepted' | 'declined' | null;
  /** Deal dates that can be changed from the calendar: the date field on the deal. */
  dealField?: 'closeDate' | 'submissionDeadline' | 'expectedPoDate' | null;
}

const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
export const calendarQuerySchema = z.object({
  from: date,
  to: date,
  /** "mine" (default), "all" (everything the user can see), or one user's id. */
  who: z.union([z.enum(['mine', 'all']), z.string().uuid()]).default('mine'),
}).refine((q) => q.from <= q.to, { path: ['to'], message: 'The end must be after the start' })
  .refine((q) => (Date.parse(q.to) - Date.parse(q.from)) / 86400_000 <= 62, { path: ['to'], message: 'Show at most two months at a time' });
export type CalendarQuery = z.infer<typeof calendarQuerySchema>;
